import React, { useEffect, useState } from 'react';
import { Music, FileText, Image, Film } from 'lucide-react';

interface InlineAssetPreviewProps {
  type: 'image' | 'video' | 'audio' | 'text';
  file?: File | null;
  /** Direct URL to use when no File object is available (e.g. asset dropped from gallery). */
  url?: string | null;
  textValue?: string | null;
}

/**
 * Lightweight inline preview shown inside a filled slot card.
 * - image  → small thumbnail
 * - video  → first-frame via seek-to-0.001
 * - audio  → music icon + filename
 * - text   → styled read-only value block
 *
 * Accepts either a `file` (local upload) or a `url` (gallery asset already in Storage).
 */
const InlineAssetPreview: React.FC<InlineAssetPreviewProps> = ({ type, file, url: directUrl, textValue }) => {
  const [objectUrl, setObjectUrl] = useState<string>('');

  useEffect(() => {
    if (!file || type === 'text') return;
    const created = URL.createObjectURL(file);
    setObjectUrl(created);
    return () => {
      URL.revokeObjectURL(created);
      setObjectUrl('');
    };
  }, [file, type]);

  const url = objectUrl || directUrl || '';

  if (type === 'text') {
    if (!textValue) return null;
    return (
      <div className="mt-2 flex items-start gap-2">
        <FileText className="w-3.5 h-3.5 text-text-tertiary flex-shrink-0 mt-0.5" />
        <p className="text-xs text-text-primary bg-bg-primary border border-border-subtle rounded px-2 py-1 flex-1 break-words">
          {textValue}
        </p>
      </div>
    );
  }

  if (!url) return null;

  if (type === 'image') {
    return (
      <div className="mt-2 flex items-center gap-2">
        <Image className="w-3.5 h-3.5 text-text-tertiary flex-shrink-0" />
        <img
          src={url}
          alt="Preview"
          className="h-14 w-auto rounded border border-border-subtle object-cover"
        />
      </div>
    );
  }

  if (type === 'video') {
    return (
      <div className="mt-2 flex items-center gap-2">
        <Film className="w-3.5 h-3.5 text-text-tertiary flex-shrink-0" />
        {/* eslint-disable-next-line jsx-a11y/media-has-caption */}
        <video
          src={url}
          muted
          preload="metadata"
          onLoadedMetadata={(e) => {
            e.currentTarget.currentTime = 0.001;
          }}
          className="h-14 w-auto rounded border border-border-subtle object-cover"
        />
      </div>
    );
  }

  if (type === 'audio') {
    return (
      <div className="mt-2 flex items-center gap-2 text-xs text-text-secondary">
        <Music className="w-3.5 h-3.5 text-text-tertiary flex-shrink-0" />
        <span className="truncate">{file?.name ?? 'Audio asset'}</span>
      </div>
    );
  }

  return null;
};

export default InlineAssetPreview;
